import { apiCall, apiClient } from '@/shared/api';

import type { EstadoUsuario, NivelRol, Perfil, SessionUser } from '../model/usuario.entity';

/**
 * Shape real del response del backend (`AuthController.getMe`,
 * `auth.controller.ts:241-261`). Coincide directamente con el
 * `SessionUser` del frontend tras el realineamiento de Fase 9.5
 * (cierra ADR-013): el adapter ya no traduce nombres ni IDs, solo
 * valida la presencia de la sesión.
 */
type MeBackendResponse = {
  user: {
    id: number;
    email: string;
    nombre: string;
    estado: EstadoUsuario;
    rol: { id: number; nombre: string; nivel: number };
    perfil: Perfil | null;
    /** Duplicado de `rol.nombre`, mantenido por el backend para clientes legacy. */
    roleName: string;
    roles: string[];
    permisos: string[];
    mfa?: {
      enabled: boolean;
      methods: Array<'totp' | 'backup_code' | 'webauthn'>;
      required: boolean;
      gracePeriodEndsAt: string | null;
    };
  } | null;
};

// Debe cubrir todo el rango de `NivelRol` (usuario.entity.ts): el
// Solicitante es `-2`. Omitirlo hacía throw en `toNivelRol` y rompía la
// hidratación de `/auth/me` para ese rol (login inutilizable).
const NIVELES_VALIDOS = new Set<number>([-2, -1, 0, 1, 2, 3, 4]);

function toNivelRol(value: number): NivelRol {
  if (!NIVELES_VALIDOS.has(value)) {
    throw new Error(`Rol.nivel inválido del backend: ${value}`);
  }
  return value as NivelRol;
}

/**
 * Lee el perfil de la sesión actual desde `GET /auth/me` y devuelve el
 * `SessionUser` para poblar el store. Si no hay sesión, lanza —
 * los callers que necesiten "no autenticado" como caso normal deben
 * capturar el throw.
 */
export async function me(): Promise<SessionUser> {
  const data = await apiCall<MeBackendResponse>(apiClient.GET('/api/v1/auth/me'));
  if (!data.user) {
    throw new Error('Sesión no autenticada.');
  }
  const u = data.user;
  return {
    id: u.id,
    email: u.email,
    nombre: u.nombre,
    estado: u.estado,
    rol: {
      id: u.rol.id,
      nombre: u.rol.nombre,
      nivel: toNivelRol(u.rol.nivel),
    },
    perfil: u.perfil,
    permisos: u.permisos,
    ...(u.mfa ? { mfa: u.mfa } : {}),
  };
}
